import type { QuickRebuttal, SalesChecklist } from "@expert/contentful";
import type { AudioType } from "@expert/shared-types";

import { useNotification } from "@expert/common-ui";
import { Box, Button, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useEffectEvent, useState } from "react";

import type { EligibleProduct } from "../../../../types";

import { createEnrollment, EnrollmentStatus, enrollmentStatusToSaleProgress } from "../../../../api/enrollment";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { sendSalesErrorMetric } from "../../../../shared-utils";
import { formatQuickRebuttal } from "../../../../shared-utils/formatQuickRebuttal";
import { useProductOfferStore } from "../../../../stores";
import { SaleProgress } from "../../../../types";
import { FullOfferChecklist } from "../FullOfferChecklist/FullOfferChecklist";
import { hasDisclosureContent } from "../FullOfferChecklist/utils/disclosureContent";
import { shouldUsePreRecordedDisclosures } from "../FullOfferChecklist/utils/shouldUsePreRecordedDisclosures";
import { LightBulbIcon } from "../Icons";
import { useLanguageStore } from "../language-selector/useLanguageStore";
import { SalesFooter } from "./SalesFooter";
import styles from "./SalesFooter.module.css";

/**
 * Failed enrollment attempts allowed per product offer before Enroll is retired for that offer.
 *
 * An AT&T HOMEPRO rejection (PO Box billing address, no subscriber record, offering already owned)
 * is deterministic: ASP returns FAILURE for every identical retry, and an identical retry has never
 * once succeeded (ew#7570 measured 167/169 upstream errors as AT&T 4xx business rejections). But
 * nothing reaching this component can tell that apart from a transient fault — `createEnrollment`
 * collapses every thrown error into the bare `ERROR` sentinel, and the deterministic rejection and a
 * genuine AT&T 502 both arrive as our own 500 anyway. So every real failure counts, and the cap is 2
 * rather than 1: one retry survives for the transient case, against a measured 20-50 re-clicks per
 * not-enrollable customer.
 *
 * Scope of the bound, stated exactly: two failed `/v2/enrollment` calls per (session,
 * productOfferId), held in the product-offer store so it survives this step unmounting and a reload.
 * It does NOT bound the *other* terminal action. If the disclosure degrades (see
 * `usePreRecordedDisclosure`) the footer reverts to Continue → SendLinkStep, which is the pre-AD/RD
 * smart-link path and never calls `/v2/enrollment` — separately capped, server-side.
 */
const MAX_ENROLLMENT_ATTEMPTS = 2;

/**
 * Shown on the toast for the capped attempt and kept on screen next to the now-dead Enroll button,
 * because a toast dismisses itself and the expert needs to still see why the button stopped working.
 * Deliberately does not name a reason: ASP's HTP adapter returns a bare FAILURE with no code and no
 * message, so we do not have one (surfacing the real AT&T text needs an ASP change, out of scope).
 * Names the two things the expert can still do from this screen. Decline stays enabled because the
 * cap leaves `saleProgress` InProgress; `SalesFooter` also gates Decline on a present `product` and
 * on not being a training task, and both hold here (a simulated voice task never renders Enroll at
 * all, so the counter cannot increment there). The offers list is live alongside in `OffersView`.
 */
const ENROLLMENT_ATTEMPTS_EXHAUSTED_MESSAGE =
    "This customer can't be enrolled here. Decline the offer or select a different one.";

interface FullOfferChecklistStepProps {
    product: EligibleProduct;
    salesChecklist?: SalesChecklist;
    quickRebuttal?: QuickRebuttal;
    sendLinkCopy?: string;
    sendLinkSubCopy?: string;
    sentSuccessMessage?: string;
    isGuidedBrowsing?: boolean;
    showContinueButton?: boolean;
    checklistPlacement?: string;
    requiresChecklistCompletion?: boolean;
    returnLinkToExpert?: boolean;
    sendLinkCommType?: string;
}

export function FullOfferChecklistStep({
    product,
    salesChecklist,
    quickRebuttal,
    sendLinkCopy,
    sendLinkSubCopy,
    sentSuccessMessage,
    isGuidedBrowsing,
    showContinueButton,
    checklistPlacement,
    requiresChecklistCompletion,
    returnLinkToExpert,
    sendLinkCommType,
}: FullOfferChecklistStepProps) {
    const {
        logger,
        onQuickRebuttal,
        features,
        sessionId,
        partner,
        apiClients,
        analytics,
        taskData,
        taskType,
        onPlayDisclosure,
        disclosureAudioAvailability,
        isSimulatedVoiceTask,
    } = useSalesContext();
    const { audioType } = useLanguageStore();
    const notification = useNotification();

    // Track which languages have had their disclosure played to completion. Enroll unlocks whenever
    // the *currently selected* language is in this set — so toggling the LanguageSelector away and
    // back to a language already played keeps Enroll enabled, while switching to a not-yet-played
    // language re-locks it. Completion is credited to the language that actually played (captured
    // in-flight by RecordedLegalDisclosuresSection), so a mid-play switch can't unlock Enroll for a
    // language the customer never heard.
    const [completedAudioTypes, setCompletedAudioTypes] = useState<ReadonlySet<AudioType>>(() => new Set());
    // Stable identity so RecordedLegalDisclosuresSection's effect (which lists this in deps)
    // doesn't re-fire and re-`restart` the timer on every parent render.
    const handleDisclosureCompleted = useCallback((playedAudioType: AudioType) => {
        setCompletedAudioTypes((prev) => (prev.has(playedAudioType) ? prev : new Set(prev).add(playedAudioType)));
    }, []);

    // Set when the disclosure audio can't be played (conference failed at play time). We then degrade
    // to the standard Continue button instead of leaving the agent stuck behind an Enroll gated on
    // audio that will never complete.
    const [disclosureAudioFailed, setDisclosureAudioFailed] = useState(false);
    // Reported inline, not via `useEffectEvent` — that may only be called from an Effect or another
    // Effect Event, and this runs from an event callback.
    // Takes the AudioType that actually failed rather than re-reading the live language selection:
    // `LanguageSelector` is only disabled while audio is *playing*, so during the up-to-7s pending
    // window the expert can switch languages, and the live selection would then be the wrong one —
    // silently swallowing the report (if the switched-to language had completed) or attributing it to
    // audio that never failed.
    const handleDisclosureAudioFailed = useCallback(
        (failedAudioType: AudioType) => {
            // A failed REPLAY of a disclosure that already played to completion for that language
            // must NOT degrade to Continue — that would discard the already-satisfied Enroll gate (the
            // customer already heard the disclosure). Only degrade when the language has never completed,
            // so audio that genuinely never played still falls back rather than stranding the expert.
            if (completedAudioTypes.has(failedAudioType)) return;
            logger.error(
                { productSku: product.productSku, partner, audioType: failedAudioType },
                "Recorded disclosure audio failed to play; degrading to Continue — the customer did NOT hear the disclosure",
            );
            // Overlaps the section's `LegalDisclosurePlayError` (every failed ATTEMPT, with a `reason`)
            // only on a first-play timeout/conference_error. This one is the compliance subset — a
            // disclosure the customer had never heard. Double-count on that one path is accepted.
            void sendSalesErrorMetric("DisclosureAudioPlayFailed", { partner, audioType: failedAudioType });
            setDisclosureAudioFailed(true);
        },
        [completedAudioTypes, logger, partner, product.productSku],
    );

    // Failed enrollment attempts for THIS offer, from the store rather than component state. Two
    // reasons, both load-bearing:
    //   - Keyed by `productOfferId`, so a capped offer never retires Enroll for an unrelated one. A
    //     session can carry several eligible primary products, and IntegratedFlow renders this step
    //     with no `key`, so selecting another offer swaps the `product` prop on the same instance.
    //   - It outlives this component. The step renders only under `SuggestedOfferStep`, so any step
    //     change unmounts it — and `useIntegratedFlowStore` persists the step, so a reload returns to
    //     this screen. A local counter would come back at zero and re-arm Enroll for the same
    //     not-enrollable customer, which is the behavior this cap exists to stop.
    // Mirrors `smartLinkMetaBySession.maximumSmartLinksGenerated` — the package's existing per-offer
    // "cap reached, retire the affordance" datum — and is cleared by resetSaleSession/endSaleSession.
    const failedAttemptsForOffer = useProductOfferStore((state) =>
        state.getFailedEnrollmentAttempts(sessionId, product.id),
    );
    const enrollmentAttemptsExhausted = failedAttemptsForOffer >= MAX_ENROLLMENT_ATTEMPTS;

    // The flag (+ SKU/voice) says we should use recorded disclosures...
    const flagWantsDisclosure = shouldUsePreRecordedDisclosures({
        productSku: product.productSku,
        features,
        taskType,
        isSimulatedVoiceTask,
    });
    // ...but only actually use them when BOTH preconditions hold for the selected language: the
    // partner has a configured disclosure endpoint, AND this SKU has the disclosure content (length +
    // copy). If either is missing — or a play attempt failed at click time — we gracefully degrade to
    // the standard Continue button, otherwise the player can't render / complete and the Enroll gate
    // could never be satisfied, leaving the expert stuck.
    const disclosureContentAvailable = hasDisclosureContent(product.productSku, audioType);
    // Read the per-language boolean once so effects can depend on it directly — depending on the
    // `disclosureAudioAvailability` object (rebuilt every context recompute) would re-fire the
    // degradation metrics repeatedly during a single call.
    const audioAvailableForLanguage = disclosureAudioAvailability[audioType];
    const usePreRecordedDisclosure =
        flagWantsDisclosure && audioAvailableForLanguage && disclosureContentAvailable && !disclosureAudioFailed;

    const requiresDisclosure = usePreRecordedDisclosure && !!onPlayDisclosure;

    // Surface each misconfiguration distinctly (flag on for a voice offer, but the selected language
    // is missing either its audio Quick Connect endpoint or its SKU disclosure content). Reported via
    // effect events so `partner`/`product` stay out of the effect deps and they don't re-fire on
    // every parent render.
    const reportMissingDisclosureAudio = useEffectEvent(() => {
        logger.error(
            { productSku: product.productSku, partner, audioType },
            "Audio disclosures flag is on but no matching audio Quick Connect endpoint is available for the selected language; falling back to Continue",
        );
        void sendSalesErrorMetric("DisclosureAudioEndpointMissing", { partner, audioType });
    });

    const reportMissingDisclosureText = useEffectEvent(() => {
        logger.error(
            { productSku: product.productSku, partner, audioType },
            "Audio disclosure endpoint exists but no disclosure content (length/copy) is configured for the selected language; falling back to Continue",
        );
        void sendSalesErrorMetric("DisclosureTextMissingForLanguage", { partner, audioType });
    });

    // Deliberately NOT keyed on `audioType`: the two derived booleans already change when the
    // selected language's availability/content actually differs. Depending on `audioType` directly
    // would re-fire the metric on every language toggle even when the misconfiguration is unchanged
    // (e.g. a partner with no ARN for either language), inflating alert volume.
    useEffect(() => {
        if (!flagWantsDisclosure) return;
        if (!audioAvailableForLanguage) reportMissingDisclosureAudio();
        else if (!disclosureContentAvailable) reportMissingDisclosureText();
    }, [flagWantsDisclosure, audioAvailableForLanguage, disclosureContentAvailable]);

    // Report the missing-checklist failure via an effect event so `product`/`partner`
    // stay out of the effect deps — otherwise a new `product` reference would re-fire
    // the metric on every parent render while `salesChecklist` is still undefined.
    const reportMissingChecklist = useEffectEvent(() => {
        logger.error({ product, salesChecklist }, "Full offer checklist failed to render");
        void sendSalesErrorMetric("FullOfferChecklistRenderError", { partner });
    });

    useEffect(() => {
        if (!salesChecklist) reportMissingChecklist();
    }, [salesChecklist]);

    const onEnroll = async () => {
        // Belt to the button's `isDisabled` brace: enforce the cap here too, so it does not depend on
        // Mantine continuing to render a native `disabled` attribute (an `aria-disabled` switch, or any
        // future second caller of onEnroll, would otherwise void the cap with no test failing).
        if (enrollmentAttemptsExhausted) return;

        void analytics.dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: product.productSku,
        });

        const enroll = {
            callId: taskData.asurionCallId ?? sessionId,
            sessionId,
            partner,
            productOfferId: product.id,
            productSku: product.productSku,
            salesProvider: product.salesProvider,
        };

        const response = await createEnrollment(apiClients.sales, logger, analytics, enroll);
        const saleProgress = enrollmentStatusToSaleProgress(response.enrollmentStatus);

        if (saleProgress === SaleProgress.Success || saleProgress === SaleProgress.Declined) {
            // SUCCESS and DECLINED are terminal — flip the offer so the existing terminal guards hide
            // the actions. DECLINED (the offer already carries a permanent declination) is an expected
            // outcome, not an error, so no error toast.
            useProductOfferStore.getState().updateProduct(sessionId, product.id, {
                ...product,
                saleProgress,
            });
        } else {
            // Not terminal for the OFFER — leave saleProgress InProgress so the async WebSocket path and
            // the Decline button keep working (flipping to Failure would swap the whole surface for
            // EnrollFailureScreen and disable Decline). Spend a strike instead, and retire Enroll once
            // the budget is gone.
            //
            // Only an actual failure costs a strike. `enrollmentStatusToSaleProgress` returns undefined
            // for any status it does not know, and the package's enrollment vocabulary includes the
            // non-terminal `PENDING` / `INITIALIZED` (api/getProductOffer.ts) on a bare `string` field.
            // Charging those would retire Enroll with nothing having gone wrong. They still raise the
            // error toast exactly as before; only the strike is withheld.
            const isFailedAttempt =
                saleProgress === SaleProgress.Failure ||
                response.enrollmentStatus?.toUpperCase() === EnrollmentStatus.Error;
            const attemptsForOffer = isFailedAttempt
                ? useProductOfferStore.getState().incrementFailedEnrollmentAttempts(sessionId, product.id)
                : failedAttemptsForOffer;
            logger.warn(
                {
                    productSku: product.productSku,
                    productOfferId: product.id,
                    sessionId,
                    partner,
                    enrollmentStatus: response.enrollmentStatus,
                    countedAsFailedAttempt: isFailedAttempt,
                    failedAttempts: attemptsForOffer,
                    maxAttempts: MAX_ENROLLMENT_ATTEMPTS,
                },
                attemptsForOffer >= MAX_ENROLLMENT_ATTEMPTS
                    ? "Enrollment did not succeed; attempt cap reached, Enroll retired for this offer"
                    : "Enrollment did not succeed",
            );
            // No sendSalesErrorMetric on the cap itself — a cap refusal is a business rule, not a new
            // fault, and `sell-error-rate-frontend` counts exwo_Error_total by {surface,component} with
            // no errorType filter, so it cannot be excluded by type (same reasoning as
            // isMaxNotificationAttemptsError in api/sendNotification.errors.ts, ew#7426). The underlying
            // failures are already attributed by createEnrollment.
            notification.error(
                attemptsForOffer >= MAX_ENROLLMENT_ATTEMPTS
                    ? ENROLLMENT_ATTEMPTS_EXHAUSTED_MESSAGE
                    : "There was an error processing the enrollment",
            );
        }
    };

    const QuickRebuttalButton = () => (
        <Button
            className={styles.quickRebuttalButton}
            variant="outline"
            radius="lg"
            fz="xs"
            size="sm"
            leftSection={<LightBulbIcon />}
            onClick={() => onQuickRebuttal?.(formatQuickRebuttal(quickRebuttal))}
            color="white"
        >
            Quick rebuttal
        </Button>
    );

    return (
        <Stack pos="relative" flex={1}>
            <Box flex={1} style={{ overflowY: "auto" }}>
                {product.saleProgress === SaleProgress.InProgress && salesChecklist ? (
                    <FullOfferChecklist
                        salesChecklist={salesChecklist}
                        productSku={product.productSku}
                        sendLinkCopy={sendLinkCopy}
                        sendLinkSubCopy={sendLinkSubCopy}
                        sentSuccessMessage={sentSuccessMessage}
                        onDisclosureCompleted={handleDisclosureCompleted}
                        onDisclosureAudioFailed={handleDisclosureAudioFailed}
                        isGuidedBrowsing={isGuidedBrowsing}
                        checklistPlacement={checklistPlacement}
                        requiresChecklistCompletion={requiresChecklistCompletion}
                        returnLinkToExpert={returnLinkToExpert}
                        sendLinkCommType={sendLinkCommType}
                    />
                ) : (
                    <Text mt="20%" c="dark.1" size="sm">
                        Offer checklist is currently unavailable. Try again later
                    </Text>
                )}
            </Box>
            {/* Deliberately tied to the Enroll button's own two conditions, not just to the cap:
                - `usePreRecordedDisclosure`: when this flips false the footer reverts to Continue →
                  SendLinkStep, a path that does not call /v2/enrollment and is capped server-side. The
                  copy below tells the expert to Decline or switch offers, which would contradict a live
                  Continue, so it goes when Enroll goes. The cap itself is NOT forgotten — it lives in
                  the store, so coming back to Enroll restores it.
                - `InProgress`: the offer can flip terminal from outside this component
                  (handleEnrollmentEvent), and Decline is disabled on that flip — leaving this up would
                  tell the expert to Decline with the Decline button already dead.
                `role="alert"` because the button it explains has just gone dead with no other cue. */}
            {usePreRecordedDisclosure &&
            enrollmentAttemptsExhausted &&
            product.saleProgress === SaleProgress.InProgress ? (
                <Text px="md" c="dark.1" size="xs" role="alert" data-testid="enrollment-attempts-exhausted">
                    {ENROLLMENT_ATTEMPTS_EXHAUSTED_MESSAGE}
                </Text>
            ) : null}
            <SalesFooter
                product={product}
                buttonSlot={quickRebuttal && <QuickRebuttalButton />}
                isGuidedBrowsing={isGuidedBrowsing}
                showContinueButton={showContinueButton}
                continueButtonProps={
                    usePreRecordedDisclosure
                        ? {
                              text: "Enroll",
                              onClick: onEnroll,
                              // Terminal offers (SUCCESS/DECLINED) must not allow another Enroll: the
                              // step isn't unmounted on flip (IntegratedFlow routes off `step`, and the
                              // footer renders outside the saleProgress guard above), so gate here too.
                              isDisabled:
                                  product.saleProgress !== SaleProgress.InProgress ||
                                  // Deterministically-failing offer: stop re-arming Enroll (ew#7570).
                                  enrollmentAttemptsExhausted ||
                                  (requiresDisclosure && !completedAudioTypes.has(audioType)),
                          }
                        : undefined
                }
            />
        </Stack>
    );
}
